// ── CIDR Utilities ─────────────────────────────────────────────

export function ipToNum(ip) {
  const p = ip.split('.').map(Number);
  return ((p[0] << 24) | (p[1] << 16) | (p[2] << 8) | p[3]) >>> 0;
}

export function ipInSubnet(ip, subnet) {
  const [base, prefix] = subnet.split('/');
  const len = parseInt(prefix);
  const mask = len === 0 ? 0 : (~0 << (32 - len)) >>> 0;
  return (ipToNum(ip) & mask) === (ipToNum(base) & mask);
}

export function getVlanForIP(ip) {
  return vlans.find(v => ipInSubnet(ip, v.subnet)) || null;
}

// ── Network Config ─────────────────────────────────────────────

export const NETWORK_BASE = [10, 0, 0, 0];
export const NETWORK_PREFIX = 22;  // 10.0.0.0 – 10.0.3.255 (1024 IPs)

// ── Known Devices ──────────────────────────────────────────────

export const knownDevices = {
  '10.1.0.1':   { hostname: 'SW-CORE-01',  os: 'Cisco IOS 15.x',      purpose: 'Core Switch',             status: 'online',  type: 'Physical' },
  '10.1.0.2':   { hostname: 'SW-CORE-01',  os: 'Cisco IOS 15.x',      purpose: 'Core Switch (MGMT)',      status: 'online',  type: 'Physical' },
  '10.1.0.50':  { hostname: 'TENABLE-01',  os: 'Red Hat 9.x',         purpose: 'Vuln Scanner',            status: 'online',  type: 'Physical' },
  '10.1.0.130': { hostname: 'MGMT-RTR-01', os: 'Cisco IOS 17.x',      purpose: 'Management Router',       status: 'online',  type: 'Physical' },
  '10.1.0.131': { hostname: 'JUMP-01',     os: 'Windows Server 2022', purpose: 'Jump Server',             status: 'online',  type: 'Physical' },
  '10.1.1.1':   { hostname: 'SW-SRV-01',   os: 'Cisco IOS 15.x',      purpose: 'Server Access Switch',    status: 'online',  type: 'Physical' },
  '10.1.1.10':  { hostname: 'SRV-DC01',    os: 'Windows Server 2022', purpose: 'Domain Controller',       status: 'online',  type: 'Physical' },
  '10.1.1.11':  { hostname: 'SRV-APP01',   os: 'Windows Server 2019', purpose: 'App Server',              status: 'online',  type: 'Physical' },
  '10.1.1.12':  { hostname: 'SRV-FILE01',  os: 'Windows Server 2022', purpose: 'File Server',             status: 'offline', type: 'Physical' },
  '10.1.1.20':  { hostname: 'SRV-DC01',    os: 'Windows Server 2022', purpose: 'Domain Controller (VIP)', status: 'online',  type: 'Physical' },
  '10.1.1.50':  { hostname: 'HV-01',       os: 'VMware ESXi 8.0',     purpose: 'Hypervisor',              status: 'online',  type: 'Physical' },
  '10.1.1.51':  { hostname: 'HV-01',       os: 'VMware ESXi 8.0',     purpose: 'Hypervisor (MGMT)',       status: 'online',  type: 'Physical' },
  '10.1.1.100': { hostname: 'VM-WEB-01',   os: 'Ubuntu 22.04',        purpose: 'Web Server (VM)',         status: 'online',  type: 'Virtual'  },
  '10.1.1.101': { hostname: 'VM-DB-01',    os: 'Ubuntu 22.04',        purpose: 'Database (VM)',           status: 'online',  type: 'Virtual'  },
  '10.1.1.102': { hostname: 'VM-APP-01',   os: 'Ubuntu 22.04',        purpose: 'App Server (VM)',         status: 'online',  type: 'Virtual'  },
  '10.1.2.1':   { hostname: 'SW-WS-01',    os: 'Cisco IOS 15.x',      purpose: 'WS Access Switch',        status: 'online',  type: 'Physical' },
  '10.1.2.10':  { hostname: 'DESKTOP-01',  os: 'Windows 11',          purpose: 'Workstation',             status: 'online',  type: 'Physical' },
  '10.1.2.45':  { hostname: 'DESKTOP-07',  os: 'Windows 11',          purpose: 'Workstation',             status: 'online',  type: 'Physical' },
  '10.1.2.88':  { hostname: 'DESKTOP-22',  os: 'Windows 10',          purpose: 'Workstation',             status: 'offline', type: 'Physical' },
};

// ── IP Range Generator ─────────────────────────────────────────

export function generateIPRange(baseOctets, prefix, deviceMap = knownDevices) {
  const total = Math.pow(2, 32 - prefix);
  const baseNum = (baseOctets[0] << 24) | (baseOctets[1] << 16) | (baseOctets[2] << 8) | baseOctets[3];
  return Array.from({ length: total }, (_, i) => {
    const n = baseNum + i;
    const ip = [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join('.');
    return deviceMap[ip]
      ? { ip, ...deviceMap[ip] }
      : { ip, hostname: null, os: null, purpose: null, status: 'unassigned', type: null, scannable: null };
  });
}

export const ipRange = generateIPRange(NETWORK_BASE, NETWORK_PREFIX);

// ── Derived Summary Data ───────────────────────────────────────

// Unique devices by hostname (deduplicate VIPs/MGMT IPs)
const uniqueDevices = Object.values(
  Object.values(knownDevices).reduce((acc, d) => {
    if (!acc[d.hostname]) acc[d.hostname] = d;
    return acc;
  }, {})
);

export const summary = {
  totalIPs: ipRange.length,
  assigned: ipRange.filter(i => i.status !== 'unassigned').length,
  online: ipRange.filter(i => i.status === 'online').length,
  offline: ipRange.filter(i => i.status === 'offline').length,
  unassigned: ipRange.filter(i => i.status === 'unassigned').length,
  totalDevices: uniqueDevices.length,

  byOS: Object.entries(
    uniqueDevices.reduce((acc, d) => {
      acc[d.os] = (acc[d.os] || 0) + 1;
      return acc;
    }, {})
  ).sort((a, b) => b[1] - a[1]),

  byType: Object.entries(
    uniqueDevices.reduce((acc, d) => {
      acc[d.type] = (acc[d.type] || 0) + 1;
      return acc;
    }, {})
  ).sort((a, b) => b[1] - a[1]),

  byPurposeCategory: Object.entries(
    uniqueDevices.reduce((acc, d) => {
      const cat =
        d.purpose.includes('Switch') ? 'Switches' :
        d.purpose.includes('Server') || d.purpose.includes('Controller') ? 'Servers' :
        d.purpose.includes('VM') || d.purpose.includes('Hypervisor') ? 'Virtualization' :
        d.purpose.includes('Scanner') ? 'Security Tools' : 'Other';
      acc[cat] = (acc[cat] || 0) + 1;
      return acc;
    }, {})
  ).sort((a, b) => b[1] - a[1]),
};

// ── VLANs ──────────────────────────────────────────────────────

export const vlans = [
  { id: 'VLAN 10', subnet: '10.1.0.0/25',  name: 'Core Infrastructure',  devices: 3,  color: '#00bcd4' },
  { id: 'VLAN 11', subnet: '10.1.0.128/25',name: 'Network Management',   devices: 2,  color: '#ff9800' },
  { id: 'VLAN 20', subnet: '10.1.1.0/24',  name: 'Server Network',       devices: 10, color: '#a855f7' },
  { id: 'VLAN 30', subnet: '10.1.2.0/24',  name: 'Workstations',         devices: 4,  color: '#4caf50' },
];

// ── Device Groups (for Assets page right panel) ────────────────

export const deviceGroups = [
  {
    id: 'network',
    label: 'Network Devices',
    devices: [
      { hostname: 'SW-CORE-01',    ips: ['10.1.0.1', '10.1.0.2 (MGMT)'],    lastSeen: '1 min ago', type: 'Physical' },
      { hostname: 'SW-SRV-01',     ips: ['10.1.1.1'],                         lastSeen: '1 min ago', type: 'Physical' },
      { hostname: 'TENABLE-01',    ips: ['10.1.0.50'],                        lastSeen: '1 min ago', type: 'Physical' },
    ],
  },
  {
    id: 'servers',
    label: 'Servers',
    devices: [
      { hostname: 'SRV-DC01',   ips: ['10.1.1.10', '10.1.1.20 (VIP)'],  lastSeen: '2 min ago', type: 'Physical' },
      { hostname: 'SRV-APP01',  ips: ['10.1.1.11'],                       lastSeen: '5 min ago', type: 'Physical' },
      { hostname: 'SRV-FILE01', ips: ['10.1.1.12'],                       lastSeen: '3 hrs ago', type: 'Physical', offline: true },
    ],
  },
  {
    id: 'hypervisors',
    label: 'Hypervisors & VMs',
    devices: [
      { hostname: 'HV-01',     ips: ['10.1.1.50', '10.1.1.51 (MGMT)'], lastSeen: '1 min ago', type: 'Physical' },
      { hostname: 'VM-WEB-01', ips: ['10.1.1.100'],                      lastSeen: '3 min ago', type: 'Virtual' },
      { hostname: 'VM-DB-01',  ips: ['10.1.1.101'],                      lastSeen: '3 min ago', type: 'Virtual' },
      { hostname: 'VM-APP-01', ips: ['10.1.1.102'],                      lastSeen: '4 min ago', type: 'Virtual' },
    ],
  },
  {
    id: 'workstations',
    label: 'Workstations',
    devices: [
      { hostname: 'DESKTOP-01', ips: ['10.1.2.10'], lastSeen: '2 min ago', type: 'Physical' },
      { hostname: 'DESKTOP-07', ips: ['10.1.2.45'], lastSeen: '8 min ago', type: 'Physical' },
      { hostname: 'DESKTOP-22', ips: ['10.1.2.88'], lastSeen: '3 hrs ago', type: 'Physical', offline: true },
    ],
  },
];
